import Table from "components/Table/Table";
import { commonColumnProps } from "constants/TableConstants";
import { useEffect } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useNavigate } from "react-router-dom";
import { setIsLoading } from "reducers/main";
import { useGetFilesQuery } from "services/file-api";
import type {
  FileType,
  ServerSortableFilesTableDataType,
} from "types/DataTypes";
import { useQuery } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

import FilePageContainer from "./FilePageContainer";

const FILE_COLUMNS = [
  { key: "id", name: "File #", ...commonColumnProps },
  { key: "last_accessed", name: "Last Viewed", ...commonColumnProps },
  { key: "client__name", name: "Client", ...commonColumnProps },
  { key: "employer__name", name: "Employer", ...commonColumnProps },
  { key: "status", name: "Status", ...commonColumnProps },
  { key: "claimants__first_name", name: "Claimant", ...commonColumnProps },
  { key: "dates_of_injury", name: "Date of Injury", ...commonColumnProps },
];

const RecentlyViewedFiles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();

  const {
    data: files = { results: [], count: 0 },
    isLoading,
    isFetching,
  } = useGetFilesQuery(
    {
      searchTerm: query.get(GLOBAL_SEARCH_PARAM),
      filters: {
        recently_viewed: true,
        limit: 50, // show up to 50 recently viewed files
      },
    },
    {},
  );

  const handleCellClick = (
    args: CellClickArgs<ServerSortableFilesTableDataType, unknown>,
  ) => {
    navigate(`/operations/files/${args.row.id}/edit/claimant-information`);
  };

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const rows =
    files.results?.map((file: FileType) => ({
      id: file.id,
      last_accessed: file.last_accessed,
      status: file.status_verbose,
      dates_of_injury: file.dates_of_injury?.[0]?.date,
      client_name: file.client?.name,
      employer_name: file.employer?.name,
      client__name: file.client?.name,
      employer__name: file.employer?.name,
      due_date: file.due_date,
      datetime_closed: file.datetime_closed,
      is_rush_verbose: file.is_rush_verbose,
      is_rush: file.is_rush_verbose,
      services: "",
      services__type: "",
      claimant_name:
        file.claimants?.length > 0
          ? `${file.claimants[0]?.first_name} ${file.claimants[0]?.last_name}`
          : "",
      claimants__first_name:
        file.claimants?.length > 0
          ? `${file.claimants[0]?.first_name} ${file.claimants[0]?.last_name}`
          : "",
      claimants__birth_date: file.claimants?.[0]?.birth_date,
      date_of_birth: file.claimants?.[0]?.birth_date,
    })) || [];

  return (
    <FilePageContainer>
      {files.results?.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "400px",
            gap: "16px",
            color: "#666",
          }}
        >
          <span className="material-icons" style={{ fontSize: "64px" }}>
            history
          </span>
          <h3 style={{ margin: 0 }}>No recently viewed files</h3>
          <p style={{ margin: 0 }}>
            Files you view will appear here for quick access
          </p>
        </div>
      ) : (
        <Table
          columns={FILE_COLUMNS}
          rows={rows}
          rowKeyGetter={(row: ServerSortableFilesTableDataType) => row.id}
          onCellClick={handleCellClick}
          isLoading={isFetching}
          fullHeight
          data-testid="recently-viewed-files-table"
        />
      )}
    </FilePageContainer>
  );
};

export default RecentlyViewedFiles;
